import { Box, Center, Spinner } from '@chakra-ui/react';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { PrivateRoute } from '@components/PrivateRoute';
import { config } from '@/config/config';
import { OtpView } from '@containers/admin/OtpView';
import { RecoverView } from '@containers/recover/RecoverView';
import { useAuthContext } from '@hooks/useAuthContext';
import ConsumptionsPage from '@pages/admin/consumptions';
import CorporationsPage from '@pages/admin/corporations';
import LoginAdminPage from '@pages/admin/login';
import UsersPage from '@pages/admin/users';
import SignaturesPage from '@pages/app/signatures';
import NewSessionPage from '@pages/app/new-session';
import DraftSessionsPage from '@pages/app/draft-sessions';
import ToBeApprovedSessionsPage from '@pages/app/tobeapproved-sessions';
import ToBeSignedSessionsPage from '@pages/app/tobesigned-sessions';
import SignedSessionsPage from '@pages/app/signed-sessions';
import ExpiredSessionsPage from '@pages/app/expired-sessions';
import RefusedSessionsPage from '@pages/app/refused-sessions';
import CanceledSessionsPage from '@pages/app/canceled-sessions';
import ArchivedSessionsPage from '@pages/app/archived-sessions';
import ContactsPage from '@pages/app/contacts';
import TemplateSessionsPage from '@pages/app/template-sessions';
import NewTemplateSessionPage from '@pages/app/new-template';
import SearchSessionsPage from '@pages/app/search-sessions';
import UserProfilePage from '@pages/app/userprofile';
import AccountValidationPage from '@pages/account-validation';
import ConfirmNewEmailPage from '@pages/confirm-new-email';
import DownloadSignedDocumentsPage from '@pages/download-signed-documents';
import ExternalSignPage from '@pages/external-sign';
import LoginPage from '@pages/login';
import OauthCallbackPage from '@pages/oauthCallback';
import PasswordRecoveryPage from '@pages/password-recovery';
import RecoverPage from '@pages/recover';
import ReinitPwdPage from '@pages/reinitpwd';
import ResultExternalSignPage from '@pages/result-external-sign';
import UnsubscribeDownloadSignedDocumentsPage from '@pages/unsubscribe-download-documents';
import SignupPage from './pages/signup';
import { MollieProvider } from './contexts/MollieContext';

const App: React.FC = () => {
    const { isUserAuthenticated, isAdminAuthenticated, isPendingOTP, isInitializing } = useAuthContext();
    if (isInitializing) {
        return (
            <Center h="100vh">
                <Box textAlign="center">
                    <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
                    <Box color="gray.600">Chargement...</Box>
                </Box>
            </Center>
        );
    }

    if (!isUserAuthenticated) {
        const searchParams = new URLSearchParams(location.search);
        if ((searchParams.size !== 0) && location.pathname.includes("/app/")) {
            localStorage.setItem("pathname", location.pathname) ;
            localStorage.setItem("search", location.search) ;
        }
    }

    return (
        <MollieProvider>
            <BrowserRouter>
                <Helmet>
                    <title>{config.app.title}</title>
                </Helmet>

                <Routes>
                    <Route path="/" element={<LoginPage />} />
                    <Route path="/account-validation" element={<AccountValidationPage />} />
                    <Route path="/confirm-new-email" element={<ConfirmNewEmailPage />} />
                    <Route path="/download-signed-documents" element={<DownloadSignedDocumentsPage />} />
                    <Route path="/external-sign" element={<ExternalSignPage />} />
                    <Route path="/oauthCallback" element={<OauthCallbackPage />} />
                    <Route path="/password-recovery" element={<PasswordRecoveryPage />} />
                    <Route path="/recover" element={<RecoverPage />} />
                    <Route path="/reinitpwd" element={<ReinitPwdPage />} />
                    <Route path="/result-external-sign" element={<ResultExternalSignPage />} />
                    <Route path="/unsubscribe-download-documents" element={<UnsubscribeDownloadSignedDocumentsPage />} />
                    <Route path="/signup" element={<SignupPage />} />

                    <Route path="/app/*" element={
                        <PrivateRoute>
                            <Routes>
                                <Route path="/signatures" element={<SignaturesPage />} />
                                <Route path="/newSignature/:sessionId" element={<NewSessionPage />} />
                                <Route path="/newSignature" element={<NewSessionPage />} />
                                <Route path="/draft" element={<DraftSessionsPage />} />
                                <Route path="/tobeApprovedDocuments" element={<ToBeApprovedSessionsPage />} />
                                <Route path="/tobeSignedDocuments" element={<ToBeSignedSessionsPage />} />
                                <Route path="/signedDocuments" element={<SignedSessionsPage />} />
                                <Route path="/expiredDocuments" element={<ExpiredSessionsPage />} />
                                <Route path="/refusedDocuments" element={<RefusedSessionsPage />} />
                                <Route path="/canceledDocuments" element={<CanceledSessionsPage />} />
                                <Route path="/archivedDocuments" element={<ArchivedSessionsPage />} />
                                <Route path="/contacts" element={<ContactsPage />} />
                                {config.app.templateManagement ? (
                                    <>
                                    <Route path="/templates" element={<TemplateSessionsPage />} />
                                    <Route path="/newTemplate" element={<NewTemplateSessionPage />} />
                                    <Route path="/newTemplate/:sessionId" element={<NewTemplateSessionPage />} />
                                    </>) : ""
                                }
                                <Route path="/search" element={<SearchSessionsPage />} />
                                <Route path="/recover" element={<RecoverView />} />
                                <Route path="/userProfile/account" element={<UserProfilePage />} />
                            </Routes>
                        </PrivateRoute>
                    } />

                    <Route path="/admin/*" element={
                        isAdminAuthenticated ? (
                            <Routes>
                                <Route path="/corporations" element={<CorporationsPage />} />
                                <Route path="/users" element={<UsersPage />} />
                                <Route path="/consumptions" element={<ConsumptionsPage />} />
                            </Routes>
                        ) : (
                            isPendingOTP ? (
                                <Routes>
                                    <Route path="/check-otp" element={<OtpView />} />
                                </Routes>
                            ) : (
                                <>
                                <Routes>
                                    <Route path="/" element={<LoginAdminPage />} />
                                </Routes>
                                <Navigate to="/admin/" replace />
                                </>
                            )
                        )
                    } />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </MollieProvider>
    );
};

export default App;